/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QCriteriaOperator} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QCriteriaOperator";
import {QQueryFilter} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QQueryFilter";


/***************************************************************************
 ** Build the values to pre-populate into a new record created from a possible
 ** value source's dropdown, from that source's own (already resolved) filter.
 **
 ** Only single-value EQUALS criteria seed a field - those are the only ones with
 ** an unambiguous "the new record must have this value" reading.  Other criteria
 ** shapes still scope the dropdown, so a record created here can fail to satisfy
 ** them; that is caught when the new id is resolved back to a possible value.
 **
 ** Seeded fields are also DISABLED on the create form, so anything seeded that is
 ** not genuinely a requirement over-constrains the new record with no way out.
 ** That is why the reads below are all conservative: only under AND is an EQUALS
 ** criterion a requirement at all, and anything ambiguous seeds nothing.
 ** (subFilters need no such treatment - under AND, the top-level criteria are
 ** requirements no matter what any nested group says.)
 **
 ** Lives in its own module rather than in DynamicSelect - its only caller - purely
 ** so that it stays testable.  jest cannot import DynamicSelect: it reaches
 ** RecordPickerModal, then RecordQuery, then MenuButton, which imports an ESM build
 ** that the CRA jest transform rejects.  Moving this function there would make
 ** dynamicSelectSeedValues.test.ts unrunnable, and the reads below - OR failing
 ** closed, join-qualified names, disagreeing duplicate EQUALS, QPossibleValue
 ** unwrapping - are the kind that want tests.
 ***************************************************************************/
export function buildSeedValuesFromFilter(filter: QQueryFilter): {[key: string]: any}
{
   //////////////////////////////////////////////////////////////////////////////////
   // under OR, an EQUALS criterion is not a requirement - "clientId=47 OR status  //
   // =ACTIVE" is satisfied by a record with neither clientId 47 nor that status.  //
   // Seeding (and disabling) clientId there would be simply wrong, so fail closed //
   // and seed nothing.                                                            //
   //////////////////////////////////////////////////////////////////////////////////
   if (filter?.booleanOperator && filter.booleanOperator !== "AND")
   {
      return ({});
   }

   const seedValues: {[key: string]: any} = {};
   const seenFieldNames = new Set<string>();

   for (let i = 0; i < (filter?.criteria?.length ?? 0); i++)
   {
      const criteria = filter.criteria[i];
      if (criteria.operator == QCriteriaOperator.EQUALS && criteria.values?.length == 1)
      {
         //////////////////////////////////////////////////////////////////////////////
         // a join-qualified name (otherTable.someField) is not a field on the table //
         // being created, so seeding it would put a value under a name the form has //
         // no input for                                                             //
         //////////////////////////////////////////////////////////////////////////////
         if (criteria.fieldName == null || criteria.fieldName.indexOf(".") > -1)
         {
            continue;
         }

         const value = criteria.values[0];

         ////////////////////////////////////////////////////////////////////////////////////
         // EQUALS null says the new record's field must be empty, which is what the form  //
         // already gives it - and seeding it would DISABLE the input, so a required field //
         // could never be filled in and the record could never be saved.  Seed nothing.   //
         ////////////////////////////////////////////////////////////////////////////////////
         if (value == null)
         {
            continue;
         }

         ///////////////////////////////////////////////////////////////////////////////////
         // an EXPRESSION - a date expression like {type: "NowWithOffset"}, or a filter   //
         // variable - is an object with no id.  Seeding it verbatim would put an object  //
         // into a scalar input and then disable it, leaving an un-editable field holding //
         // something the form cannot render.  Ambiguous, so seed nothing, per this       //
         // module's rule above.                                                          //
         ///////////////////////////////////////////////////////////////////////////////////
         if (typeof value === "object" && !("id" in value))
         {
            continue;
         }

         ////////////////////////////////////////////////////////////////////////
         // a possible-value criterion's value may have been cleaned up into a //
         // QPossibleValue object (id + label) - the form wants just the id.   //
         ////////////////////////////////////////////////////////////////////////
         const seedValue = (typeof value === "object" && "id" in value) ? value.id : value;

         //////////////////////////////////////////////////////////////////////////////////
         // two EQUALS on the same field, ANDed, is unsatisfiable unless they agree - so //
         // rather than letting the later one silently win, seed neither                 //
         //////////////////////////////////////////////////////////////////////////////////
         if (seenFieldNames.has(criteria.fieldName))
         {
            if (seedValues[criteria.fieldName] !== seedValue)
            {
               delete seedValues[criteria.fieldName];
            }
            continue;
         }

         seenFieldNames.add(criteria.fieldName);
         seedValues[criteria.fieldName] = seedValue;
      }
   }

   return (seedValues);
}
